import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { Priority, TaskCategory, Recurrence, CATEGORIES, Profile, Task } from '../types';

const TODAY = new Date().toISOString().slice(0, 10);

// Default deadline = end of today (23:59)
function defaultDeadline() {
  return `${TODAY}T23:59`;
}

export default function CreateTask() {
  const { user, role, profile } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('Medium');
  const [category, setCategory] = useState<TaskCategory>('General');
  const [recurrence, setRecurrence] = useState<Recurrence>('none');
  const [hasDeadline, setHasDeadline] = useState(true);
  const [deadline, setDeadline] = useState(defaultDeadline());
  const [progress, setProgress] = useState(0);
  const [developers, setDevelopers] = useState<Profile[]>([]);
  const [testers, setTesters] = useState<Profile[]>([]);
  const [assignedDeveloperId, setAssignedDeveloperId] = useState<string>('');
  const [assignedTesterId, setAssignedTesterId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    const loadMembers = async () => {
      if (!profile?.company_id) return;
      try {
        const rows = await api.get<Profile[]>('/api/users');
        const devs = rows.filter((p) => p.role === 'developer' || p.role === 'admin');
        const qas = rows.filter((p) => p.role === 'tester');
        setDevelopers(devs);
        setTesters(qas);
        if (role === 'admin') setAssignedDeveloperId(devs[0]?.user_id ?? user?.id ?? '');
        else setAssignedDeveloperId(user?.id ?? '');
      } catch {
        // silently ignore — assignee dropdowns will be empty
      }
    };
    loadMembers();
  }, [profile?.company_id, role, user?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { showToast('Title is required.', 'error'); return; }
    setSubmitting(true);

    const status = progress > 0 ? 'In Progress' : 'Pending';
    const review_status = assignedTesterId ? 'ready_for_review' : 'not_required';

    try {
      await api.post<Task>('/api/tasks', {
        title: title.trim(),
        description: description.trim(),
        priority,
        status,
        category,
        progress,
        deadline: hasDeadline ? new Date(deadline).toISOString() : null,
        task_date: TODAY,
        recurrence,
        assigned_developer_id: assignedDeveloperId || user!.id,
        assigned_tester_id: assignedTesterId || null,
        review_status,
      });

      showToast('Task added to today\'s list!', 'success');
      navigate('/tasks');
    } catch (err: any) {
      showToast(err.message || 'Failed to create task.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <h1 className="page-title">New Task</h1>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="title">Title <span className="required">*</span></label>
            <input id="title" type="text" className="form-input"
              placeholder="What do you need to accomplish today?"
              value={title} onChange={e => setTitle(e.target.value)}
              maxLength={200} required />
            <span className="char-count">{title.length}/200</span>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="description">Description</label>
            <textarea id="description" className="form-textarea"
              placeholder="Add details, steps, or acceptance criteria…"
              value={description} onChange={e => setDescription(e.target.value)} rows={4} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="category">Category</label>
              <select id="category" className="form-select"
                value={category} onChange={e => setCategory(e.target.value as TaskCategory)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="priority">Priority</label>
              <select id="priority" className="form-select"
                value={priority} onChange={e => setPriority(e.target.value as Priority)}>
                <option value="High">🔴 High</option>
                <option value="Medium">🟡 Medium</option>
                <option value="Low">🟢 Low</option>
              </select>
            </div>
          </div>
          {role === 'admin' && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Assign Developer</label>
                <select className="form-select" value={assignedDeveloperId} onChange={(e) => setAssignedDeveloperId(e.target.value)}>
                  {developers.map((dev) => (
                    <option key={dev.user_id} value={dev.user_id}>{dev.full_name || dev.email}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Assign Tester</label>
                <select className="form-select" value={assignedTesterId} onChange={(e) => setAssignedTesterId(e.target.value)}>
                  <option value="">None</option>
                  {testers.map((tester) => (
                    <option key={tester.user_id} value={tester.user_id}>{tester.full_name || tester.email}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="recurrence">Recurrence
                <span className="tooltip" data-tip="Automatically adds this task for the next occurrence when completed">?</span>
              </label>
              <select id="recurrence" className="form-select"
                value={recurrence} onChange={e => setRecurrence(e.target.value as Recurrence)}>
                <option value="none">One-time</option>
                <option value="daily">🔁 Daily</option>
                <option value="weekly">📅 Weekly</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">
                <input type="checkbox" checked={hasDeadline}
                  onChange={e => setHasDeadline(e.target.checked)}
                  style={{ marginRight: 8 }} />
                Set deadline
              </label>
              {hasDeadline && (
                <input type="datetime-local" className="form-input"
                  value={deadline} onChange={e => setDeadline(e.target.value)}
                  min={`${TODAY}T00:00`} />
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Initial Progress: <strong>{progress}%</strong>
              <span className="tooltip" data-tip="Set if you've already started this task">?</span>
            </label>
            <input type="range" min={0} max={100} step={5}
              value={progress} onChange={e => setProgress(Number(e.target.value))}
              className="progress-slider progress-slider-full" />
            <div className="task-progress-track" style={{ marginTop: 6 }}>
              <div className="task-progress-fill progress-active" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? <><span className="btn-spinner" /> Adding…</> : <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Task
              </>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
